import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail, sendResendEmail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ListChecks, Users } from "lucide-react";

const empty = { title: "", description: "", assignees: [], due_date: "", priority: "normal" };
const PRIORITY = { high: { label: "Yüksek", cls: "bg-destructive/20 text-destructive border-destructive/40" }, normal: { label: "Normal", cls: "bg-secondary text-muted-foreground border-border" }, low: { label: "Düşük", cls: "bg-primary/15 text-primary border-primary/30" } };

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function Todos() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/todos").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  const toggleAssignee = (id) => {
    setForm((f) => ({
      ...f,
      assignees: f.assignees.includes(id) ? f.assignees.filter((a) => a !== id) : [...f.assignees, id],
    }));
  };

  const save = async () => {
    if (!form.title) return toast.error("Başlık zorunlu");
    try {
      await api.post("/todos", { ...form, due_date: form.due_date || null });
      toast.success("Görev eklendi");
      setOpen(false);
      
      // Otomatik E-posta Bildirimi: Görev Bildirim Alıcılarına Mail Gönder
      try {
        const config = JSON.parse(localStorage.getItem("1011_email_automation_config") || "{}");
        const recipients = config.todoEmails || "";
        if (recipients.trim()) {
          const priorityMap = { high: "🔴 Yüksek", normal: "🟡 Normal", low: "🟢 Düşük" };
          const assigneeNames = (form.assignees || [])
            .map((aid) => userMap[aid]?.name)
            .filter(Boolean)
            .join(", ") || "Atanmadı";

          await sendResendEmail({
            to: recipients,
            subject: `📌 Yeni Görev: ${form.title}`,
            html: `
              <div style="font-family:Arial,sans-serif;padding:24px;background:#0f172a;color:#f8fafc;border-radius:12px;">
                <h2 style="color:#38bdf8;margin-top:0;">📝 10-11 Görev Bildirimi</h2>
                <table style="width:100%;color:#cbd5e1;font-size:14px;">
                  <tr><td style="padding:8px 0;"><strong>Görev Başlığı:</strong></td><td style="font-weight:bold;color:#f8fafc;">${form.title}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Açıklama:</strong></td><td>${form.description || "-"}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Öncelik:</strong></td><td>${priorityMap[form.priority] || "Normal"}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Bitiş Tarihi:</strong></td><td>${form.due_date || "-"}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Atanan Kişiler:</strong></td><td>${assigneeNames}</td></tr>
                </table>
                <hr style="border:0;border-top:1px solid #334155;margin:16px 0;" />
                <p style="font-size:11px;color:#64748b;">10-11 Yönetim Platformu Otomatik Bildirim</p>
              </div>
            `
          });
        }
      } catch (emailErr) {
        console.warn("Todo email notification failed:", emailErr);
      }

      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleDone = async (t) => {
    await api.put(`/todos/${t.id}`, { completed: !t.completed });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/todos/${id}`);
    toast.success("Silindi");
    load();
  };

  const active = items.filter((t) => !t.completed);
  const done = items.filter((t) => t.completed);

  const TodoCard = (t, i) => (
    <motion.div key={t.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ delay: i * 0.03 }}>
      <Card className={`bg-card border-border p-4 flex gap-3 hover:border-primary/40 transition-colors ${t.completed ? "opacity-60" : ""}`}>
        <Checkbox data-testid={`toggle-todo-${t.id}`} checked={t.completed} onCheckedChange={() => toggleDone(t)} className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`font-semibold ${t.completed ? "line-through" : ""}`}>{t.title}</h4>
            <Badge variant="outline" className={`text-[11px] shrink-0 ${PRIORITY[t.priority]?.cls || PRIORITY.normal.cls}`}>{PRIORITY[t.priority]?.label || "Normal"}</Badge>
          </div>
          {t.description && <p className="text-sm text-muted-foreground mt-1">{t.description}</p>}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {t.due_date && <span className="text-xs text-muted-foreground">📅 {t.due_date}</span>}
            {(t.assignees || []).map((aid) => (
              <span key={aid} className="inline-flex items-center gap-1 bg-primary/15 text-primary text-xs px-2 py-0.5 rounded-full">
                <span className="w-4 h-4 rounded-full bg-primary text-white text-[9px] flex items-center justify-center">{initials(userMap[aid]?.name)}</span>
                {userMap[aid]?.name || "?"}
              </span>
            ))}
          </div>
        </div>
        <Button size="icon" variant="ghost" data-testid={`delete-todo-${t.id}`} onClick={() => remove(t.id)} className="h-8 w-8 text-destructive hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></Button>
      </Card>
    </motion.div>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-2">Ekip</p>
          <h1 className="font-anton text-4xl sm:text-5xl">YAPILACAKLAR</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-todo-button" onClick={() => setForm(empty)} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Görev Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-anton text-2xl">YENİ GÖREV</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Başlık</Label>
                <Input data-testid="todo-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ne yapılacak?" />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea data-testid="todo-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detay (isteğe bağlı)" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Bitiş Tarihi</Label>
                  <Input type="date" data-testid="todo-due-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Öncelik</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="todo-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">Yüksek</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="low">Düşük</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Kişileri Etiketle</Label>
                <div className="flex flex-wrap gap-2" data-testid="assignee-picker">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      data-testid={`assignee-${u.id}`}
                      onClick={() => toggleAssignee(u.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.assignees.includes(u.id) ? "bg-primary text-white border-primary" : "bg-secondary text-muted-foreground border-border hover:border-primary/40"}`}
                    >
                      {u.name}
                    </button>
                  ))}
                  {users.length === 0 && <span className="text-sm text-muted-foreground">Kayıtlı kullanıcı yok</span>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-todo-button" onClick={save} className="bg-primary hover:bg-primary/90 text-white">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-5 h-5 text-primary" />
            <h3 className="font-anton text-2xl">AKTİF ({active.length})</h3>
          </div>
          <div className="space-y-2" data-testid="active-todos">
            <AnimatePresence>{active.map(TodoCard)}</AnimatePresence>
            {active.length === 0 && <Card className="bg-card border-border p-8 text-center text-muted-foreground text-sm">Aktif görev yok.</Card>}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-anton text-2xl text-muted-foreground">TAMAMLANAN ({done.length})</h3>
          </div>
          <div className="space-y-2" data-testid="done-todos">
            <AnimatePresence>{done.map(TodoCard)}</AnimatePresence>
            {done.length === 0 && <Card className="bg-card border-border p-8 text-center text-muted-foreground text-sm">Henüz tamamlanan görev yok.</Card>}
          </div>
        </div>
      </div>
    </div>
  );
}
